import {partition} from 'lodash';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {TeammateModel} from '../../../../../../../libs/shared/core/src/models/api/teammateModel';
import {buildTeammateDisplayName} from '../../../../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {getFlatActiveTeammates} from '../../../../../../../libs/shared/core/src/store/data/teammates/commonTeammatesSelectors';
import {TeammateAvatar} from '../../../../../../../libs/shared/ui/src/components/avatar/teammateAvatar';
import {boxedInputColorReaders} from '../../../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputConstants';
import {DropdownBoxCoordinator} from '../../../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {Button} from '../../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../../../../../libs/shared/ui/src/styles/commonStyles';
import {useAppSelector} from '../../../../../../helpers/appReduxHelpers';
import {
  SearchTeammateDropdown,
  SearchTeammateDropdownProps,
} from '../../../../../common/dropdowns/search/searchTeammateDropdown';

/*
 * Props.
 */

export interface SingleSelectTeammateFieldProps
  extends Pick<SearchTeammateDropdownProps, 'onChange' | 'onDelete'> {
  selectedTeammateAlias: string | undefined;
  dropdownTitle?: ReactNode;
}

/*
 * Styles.
 */

const StyledPlaceholderSpan = styled.span`
  color: ${boxedInputColorReaders.textPlaceholder};
`;

const StyledButtonContentDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

/*
 * Components.
 */

export const SingleSelectTeammateField: FC<SingleSelectTeammateFieldProps> = (props) => {
  const {selectedTeammateAlias, dropdownTitle, onChange, onDelete} = props;

  // Use blocked and visitor teammates in case the user explicitly wants to use one.

  const teammates = useAppSelector(getFlatActiveTeammates);
  const [selectedTeammates, otherTeammates] = partition(
    teammates,
    (teammate) => teammate.alias === selectedTeammateAlias,
  );
  const sortedTeammates = [...selectedTeammates, ...otherTeammates];

  // We might want to remove aliases that do not correspond to any teammates.

  return (
    <DropdownBoxCoordinator
      leftIcon={selectedTeammates.length === 0 ? 'participantSmall' : undefined}
      value={renderInputTitle(selectedTeammates)}
      renderDropdown={(popoverProps) => (
        <SearchTeammateDropdown
          {...popoverProps}
          teammates={sortedTeammates}
          selectedEmail={selectedTeammates[0]?.email}
          onChange={onChange}
          headerTitle={dropdownTitle}
          headerRightIcon={
            selectedTeammateAlias && onDelete ? (
              <Button
                variant={ButtonVariantsEnum.LINK}
                size={VisualSizesEnum.SMALL}
                onClick={() => {
                  onDelete();
                  popoverProps.onRequestClose();
                }}
              >
                <Trans id="clear-refinement-button-link">Clear</Trans>
              </Button>
            ) : null
          }
        />
      )}
      shouldDisablePadding
    />
  );
};

function renderInputTitle(selectedTeammates: ReadonlyArray<TeammateModel>) {
  const firstSelectedTeammate = selectedTeammates[0];
  if (!firstSelectedTeammate) {
    return (
      <StyledPlaceholderSpan>
        <Trans id="search-query-builder-teammate-placeholder">Select teammate</Trans>
      </StyledPlaceholderSpan>
    );
  }

  const firstTeammate = (
    <StyledButtonContentDiv>
      <TeammateAvatar teammate={firstSelectedTeammate} size={VisualSizesEnum.SMALL} />
      {buildTeammateDisplayName(firstSelectedTeammate)}
    </StyledButtonContentDiv>
  );

  return firstTeammate;
}
